<template>
  <div ref="configureTab">
    <panel-block
      v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.inverseLabelable)"
      id="pn-inverse-name"
    >
      <panel-item>
        <label
          :for="selectedObjectModelRelation.idForProp('inverse-name')"
        >Role of Related Object Model(s)</label>
        <expandable-textarea
          :id="selectedObjectModelRelation.idForProp('inverse-name')"
          v-model="selectedObjectModelRelation.inverse_label_title"
          placeholder="Enter relationship name..."
        ></expandable-textarea>
      </panel-item>
    </panel-block>

    <panel-block id="pn-related-object-type" v-if="!isAccountRelationship">
      <panel-item>
        <label for="related-object-type" class="a-select-label">Related to Which Object Models?</label>
        <a-select
          id="related-object-type"
          v-model="selectedObjectModelRelation.related_object_model_ids"
          :options="modelSearchOptions"
          bind-by-array
          multiselect
          @search:finished="addModelsToRepo"
          :selected-option-ids="selectedObjectModelRelation.related_object_model_ids.slice()"
          compress
        ></a-select>
        <pill
          v-for="model in relatedObjectModels"
          :key="model.id"
          :value="model.title"
          full
          :color="relatedModelTagColor"
          @onRemove="tryRemoveRelatedObjectModel(model.id)"
        ></pill>
      </panel-item>
    </panel-block>

    <requireable
      v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.requireable)"
      :model="selectedObjectModelRelation"
    >Require {{selectedObjectModelRelation.labelTitleOr('relationship')}} to have {{selectedObjectModelRelation.inverseLabelTitleOr('an inverse relationship')}}?</requireable>

    <panel-block
      id="pn-delete-agents"
      v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.inverseDependentDestroy)"
    >
      <panel-item>
        <label :for="selectedObjectModelRelation.idForProp('inverse_dependent_destroy')">
          Delete all {{selectedObjectModelRelation.inverseLabelTitleOr('inverse relationships')}} when the {{selectedObjectModelRelation.labelTitleOr('relationship')}} is deleted?
          <property-change-detector
            :left="selectedObjectModelRelation"
            :right="selectedObjectModelRelation.publishedEquivalent"
            :properties="[['inverse_dependent_destroy']]"
          />
        </label>
        <toggle-select
          :id="selectedObjectModelRelation.idForProp('inverse_dependent_destroy')"
          compress
          v-model="selectedObjectModelRelation.inverse_dependent_destroy"
        ></toggle-select>
      </panel-item>
    </panel-block>

    <confirmation-modal
      v-if="modalStateFor(vueModalID.confirmChange).isVisible"
      :state="modalStateFor(vueModalID.confirmChange)"
      :override-config="warningConfig"
      @toggle-modal="handleConfirmation"
    />

    <panel-block id="pn-parent-object-display-properties" v-if="!isAccountRelationship">
      <panel-item>
        <row>
          <column>
            <label
              :for="`related-object-properties-${this.selectedObjectModel.id}`"
              class="a-select-label"
            >Search by Which Indexed Properties for {{selectedObjectModel.title}}?</label>
          </column>
          <suffix>
            <help-tooltip
              text="Choose indexed properties to search & display about the records in the current object model to confirm the user is establishing a relationship with the correct record."
            ></help-tooltip>
          </suffix>
        </row>
        <a-select
          :id="`related-object-properties-${this.selectedObjectModel.id}`"
          :options="displayPropertyOptions(this.selectedObjectModel)"
          :selected-option-ids="selectedParentPropertyIds.slice()"
          bind-by-object
          multiselect
          compress
          @input="onSelectParentDisplayProperty($event)"
          :placeholder="selectedParentPropertyIds.length > 3 ? 'Max of 4 Properties Allowed' : 'Select one...'"
          :disabled="selectedParentPropertyIds.length > 3"
        ></a-select>
        <pill
          v-for="property in selectedParentDisplayProperties(this.selectedObjectModel)"
          :key="selectedPropertyKey(property)"
          :value="selectedPropertyName(property)"
          full
          :color="relatedModelTagColor"
          @onRemove="tryRemoveParentDisplayProperty(property)"
        ></pill>
      </panel-item>
    </panel-block>

    <panel-block
      id="pn-related-object-properties"
      v-for="model in relatedObjectModels"
      :key="model.id"
      v-if="!isAccountRelationship"
    >
      <panel-item>
        <row>
          <column>
            <label
              :for="`related-object-properties-${model.id}-inverse`"
              class="a-select-label">
              Search by Which Indexed Properties for {{model.title}}?
            </label>
          </column>
          <suffix>
            <help-tooltip text="Choose indexed properties to search & display about the related record to confirm the user is establishing a relationship with the correct record."></help-tooltip>
          </suffix>
        </row>
        <a-select
          :id="`related-object-properties-${model.id}-inverse`"
          :options="displayPropertyOptions(model)"
          bind-by-object
          multiselect
          compress
          :selected-option-ids="selectedRelatedPropertyIds(model).slice()"
          @input="onSelectDisplayProperty($event, model)"
          :placeholder="selectedRelatedPropertyIds(model).length > 3 ? 'Max of 4 Properties Allowed' : 'Select one...'"
          :disabled="selectedRelatedPropertyIds(model).length > 3"
        ></a-select>
        <pill
          v-for="property in selectedDisplayProperties(model)"
          :key="selectedPropertyKey(property)"
          :value="selectedPropertyName(property)"
          full
          :color="relatedModelTagColor"
          @onRemove="tryRemoveDisplayProperty(property)"
        ></pill>
      </panel-item>
    </panel-block>

    <div v-if="false"> <!-- disabled until implementation story -->
      <panel-block  v-for="model in relatedObjectModels" :key="model.id">
        <panel-item>
          <label for="related-object-list-view" class="a-select-label">
            Display Which List View for {{model.title}}?
            <help-tooltip text="Choose which list view to display about the related records to confirm the user is establishing a relationship with the correct record. Searching for existing records will use the search bar configured in your list view so ensure the list view has a search bar configured."></help-tooltip>
          </label>

          <a-select
            :id="`related-object-type-${model.id}`"
            :options="listViewOptions(model.id)"
            compress
            placeholder="Search list views"
            @input="value => onSelectListView(model.id, value)"
            ></a-select>

        </panel-item>
      </panel-block>
    </div>

    <template v-if="selectedObjectModelRelation.can(ObjectModelRelationCapability.accountRelationUserTypeable)">
      <panel-block id="pn-type-of-user-relationship">
        <panel-item>
          <label for="user-type">
            Type of user relationship?
          </label>
          <a-select
            id="user-type"
            v-model="selectedObjectModelRelation.user_type"
            :options="userTypeOptions"
            compress
            :disabled="!canChangeUserType"
          ></a-select>
        </panel-item>
      </panel-block>

      <panel-block
        id="pn-user-relationship-group-filter"
        v-if="selectedObjectModelRelation.isFormabilityUserType"
      >
        <panel-item>
          <label for="user-type-filters">
            Who can be referenced?
          </label>
          <a-select
            id="user-type-filters"
            :options="userTypeFiltersOptions"
            :selected-option-ids="selectedObjectModelRelation.userTypeFiltersHandler.map(x => x.reference_id)"
            @input="createUserTypeFilter"
            @destroy="destroyUserTypeFilter"
            bind-by-object
            multiselect
            compress
            :placeholder="userTypeFilterPlaceholder"
          ></a-select>
          <scroll-area>
            <ul class="tag-listing">
              <li v-for="filter in selectedObjectModelRelation.user_type_filters">
                <template v-if="filter.reference_type === assignableEnum.group">
                  <pill :color="filter.tagColor"
                        @onRemove="filter.destroy()"
                        :value="`${filter.display}`"
                  ></pill>
                </template>
              </li>
            </ul>
          </scroll-area>
        </panel-item>
      </panel-block>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import selectedMixin from "../mixins/selectedMixin";
import { ObjectModelRelationCapability } from "~/models/objectModelRelations/objectModelRelationCapability";
import Requireable from "~/components/modelProperties/requireable.vue";
import { StringHelper } from "~/helpers/stringHelper";
import stringHelpers from "~/helpers/stringHelpers";
import { RouteHelper } from "~/helpers/routeHelper";
import { IObjectModelsSearchResponse, IServerData } from "~/interfaces/serverInterfaces";
import {IVOptionSetLocal, IVOptionSetEndpoint, IDefaultVOption} from "~/interfaces/vSelectInterfaces"
import ObjectModel from "~/models/objectModel";
import { Route } from "~/enums/route";
import ChangeDetector from "~/services/changeDetector";
import ConfirmationModal from "~/components/modals/confirmationModal.vue";
import { IConfirmModalConfig } from "~/interfaces/modalInterfaces";
import ObjectModelRelationDefinition from "~/models/objectModelRelations/objectModelRelationDefinition";
import PropertyChangeDetector from "./property-change-detector.vue";
import ListView from "~/models/listViews/listView";
import {EnumHelper} from "~/helpers/enumHelper";
import ObjectModelDefaultPropertyReference from "~/models/objectModels/objectModelDefaultPropertyReference";
import {
  ObjectModelBuiltinPropertyType,
  ObjectModelBuiltinPropertyTypeName
} from "~/enums/objectModelBuiltinPropertyType";
import { IAccountRelationUserTypeable } from '~/interfaces/propertyInterfaces'
import { ModelType } from '~/enums/modelType'
import {PermitteeType} from '~/enums/permitteeType'
import ObjectModelRelationUserTypeFilterPermittee from '~/models/objectModelRelations/objectModelRelationUserTypeFilterPermittee'
import assignableMixin from "~/mixins/assignableMixin";
import pluralizeable from '~/mixins/pluralizeable'
import PermitteeVOptionSetMixin from '~/mixins/permitteeVOptionSetMixin';
import AppGroupPermitteeSearchMixin from '~/mixins/appGroupPermitteeSearchMixin';

const userTypeNoFilterPlaceholder = 'Anyone in the organization'

type TUserTypeVOption = IDefaultVOption<IAccountRelationUserTypeable['user_type']>;
type TAnyoneUserTypeFilterVOption = IDefaultVOption<string>;

export default Vue.extend({
  components: {
    Requireable,
    ConfirmationModal,
    PropertyChangeDetector
  },
  mixins: [
    selectedMixin,
    stringHelpers,
    assignableMixin,
    pluralizeable,
    PermitteeVOptionSetMixin,
    AppGroupPermitteeSearchMixin,
  ],
  data: function() {
    return {
      ObjectModelRelationCapability,
      StringHelper,
      relatedModelTagColor: "Orange",
      selectedRelatedObjectModelId: null,
      userTypeFiltersOptions: [],
    };
  },
  async beforeMount() {
    let res: any = await ObjectModel.provider.fetchRelatedListViews(this.selectedObjectModel);
    res[0].list_views.forEach(listViewJson => {
      new ListView().loadFromJson(listViewJson);
    });
  },
  computed: {
    application_id(): string {
      return this.selectedObjectModel.application_id;
    },
    modelSearchOptions(): IVOptionSetEndpoint {
      return {
        valueKey: "id",
        labelKey: "title",
        endpoint: RouteHelper.path({ route: Route.searchObjectModels }),
        endpointOptions: {
          where: {
            application_id: this.selectedObjectModel.application_id,
          }
        },
        rootKey: "object_models",
        selectedColor: this.relatedModelTagColor
      };
    },
    relatedObjectModels(): ObjectModel[] {
      return ObjectModel.repo.findWhere(om =>
        this.selectedObjectModelRelation.related_object_model_ids.includes(om.id)
      );
    },
    warningConfig(): Partial<IConfirmModalConfig> {
      return ObjectModelRelationDefinition.changeWarningConfig();
    },
    selectedParentPropertyIds(): string[]{
      const selectedObjectModelIds = this.selectedObjectModelRelation.parent_object_model_display_properties.slice();
      const defaultPropertiesSelected = this.selectedObjectModelRelation.parentObjectModelDisplayDefaultPropertiesArray.filter(p => p.model_id === this.selectedObjectModel.id).map(p=>p.type);
      return [...selectedObjectModelIds, ...defaultPropertiesSelected]
    },
    isAccountRelationship(): boolean {
      return this.selectedObjectModelRelation.isAccountRelationship;
    },
    userTypeOptions(): IVOptionSetLocal<TUserTypeVOption> {
      const options: Array<TUserTypeVOption> = [
        {label: 'Portal', value: ModelType.PortalUser},
        {label: 'Formability', value: ModelType.FormabilityUser},
      ]
      return {
        options,
        selectedOption: options.find(o => o.value === this.selectedObjectModelRelation.user_type),
      }
    },
    noFilterVOptionSet(): IVOptionSetLocal<TAnyoneUserTypeFilterVOption> {
      const noFilterOptions: Array<TAnyoneUserTypeFilterVOption> = [
        { label: userTypeNoFilterPlaceholder, value: null },
      ]

      return {
        isPinned: true,
        options: noFilterOptions,
        selectedOption: this.selectedObjectModelRelation.userTypeFiltersHandler.isPresent ? null : noFilterOptions[0],
      };
    },
    anyoneInAppVOption(): TAnyoneUserTypeFilterVOption {
      return {
        label: 'Anyone in the app',
        value: PermitteeType.anyoneInApp.toString(),
      };
    },
    anyoneInAppVOptionSet(): IVOptionSetLocal<TAnyoneUserTypeFilterVOption> {
      return {
        isPinned: true,
        options: [this.anyoneInAppVOption],
        optionType: parseInt(this.anyoneInAppVOption.value),
        selectedOption: this.isOnlySelectedOption(this.anyoneInAppVOption) ? this.anyoneInAppVOption : null,
      };
    },
    userTypeFilterPlaceholder(): string {
      if (this.isOnlySelectedOption(this.anyoneInAppVOption)) return 'Anyone in the app';

      const filterCount = this.selectedObjectModelRelation.userTypeFiltersHandler.all.length
      if (filterCount > 0) {
        return `${ this.$options.filters.pluralize('group', filterCount) } selected`;
      }

      return userTypeNoFilterPlaceholder;
    },
    canChangeUserType(): boolean {
      return !Boolean(this.selectedObjectModelRelation.publishedEquivalent)
    },
    assigneeCollection() {
      return this.selectedObjectModelRelation.userTypeFiltersHandler.all
    },
  },
  methods: {
    updateAppGroupVOptionSets() {
      this.userTypeFiltersOptions = [
        this.anyoneInAppVOptionSet,
        this.noFilterVOptionSet,
        this.appGroupsVOptionSet,
        this.appAssignedOrgGroupsVOptionSet,
      ];
    },
    isOnlySelectedOption(option: IDefaultVOption): boolean {
      const selectedOptions = this.selectedObjectModelRelation.userTypeFiltersHandler.all;

      return selectedOptions.length === 1 && selectedOptions[0].reference_id.toString() === option.value.toString();
    },
    selectedRelatedPropertyIds(model): string[]{
      const selectedObjectModelIds = this.selectedObjectModelRelation.related_object_model_display_properties.slice();
      const defaultPropertiesSelected = this.selectedObjectModelRelation.relatedObjectModelDisplayDefaultPropertiesArray.filter(p => p.model_id === model.id).map(p=>p.type);
      return [...selectedObjectModelIds, ...defaultPropertiesSelected]
    },
    selectedPropertyKey(property):string {
     return  property.id || `${property.model_id}-${property.type}`
    },
    selectedPropertyName(property): string {
      return property.labelTitleDisplay || ObjectModelBuiltinPropertyTypeName[property.type]
    },
    displayPropertiesForModel(model) {
      const modelIds = Object.keys(model.object_model_properties)
      const displayProps = this.selectedObjectModelRelation.related_object_model_display_properties
      return displayProps.filter(prop => modelIds.includes(prop))
    },
    listViewOptions(objectModelId): IVOptionSetEndpoint {
      const listViewId = this.selectedObjectModelRelation.relatedObjectModelListViewIdsObj[objectModelId];
      const listView = ListView.repo.findWhere(lv => listViewId === lv.id)[0];

      return {
        valueKey: "id",
        labelKey: "title",
        endpoint: `/object_models/${objectModelId}/search_list_views`,
        endpointFilters: { per_page: 5 },
        rootKey: "list_views",
        selectedOption: listView
      };
    },
    selectedDisplayProperties(model) {
      const displayProperties = this.selectedObjectModelRelation.related_object_model_display_properties.map(e => e);
      const selected = model.objectModelPropertiesArray.filter(e => displayProperties.includes(e.client_id));
      const defaultSelected = this.selectedObjectModelRelation.relatedObjectModelDisplayDefaultPropertiesArray.filter(p => p.model_id === model.id);
      return [...selected, ...defaultSelected];
    },
    selectedParentDisplayProperties(model) {
      const displayProperties = this.selectedObjectModelRelation.parent_object_model_display_properties.map(e => e);
      const selected = model.objectModelPropertiesArray.filter(e => displayProperties.includes(e.client_id));
      const defaultSelected = this.selectedObjectModelRelation.parentObjectModelDisplayDefaultPropertiesArray.filter(p => p.model_id === model.id)
      return [...selected, ...defaultSelected];
    },
    displayPropertyOptions(objectModel: ObjectModel): IVOptionSetLocal[] {
      return [
        this.objectModelIndexedPropertiesVOptionSetFor(objectModel),
        this.objectModelDefaultPropertyVOptionSetsFor(objectModel)
      ];
    },
    objectModelIndexedPropertiesVOptionSetFor(objectModel: ObjectModel): IVOptionSetLocal{
      return {
        labelKey: "labelTitleDisplay",
        valueKey: "client_id",
        options: objectModel.objectModelPropertiesArray.filter(prop => prop.isIndexed),
        optionType: "property"
      };

    },
    objectModelDefaultPropertyVOptionSetsFor(objectModel: ObjectModel): IVOptionSetLocal {
      return {
        heading: "About Record",
        valueKey: "client_id",
        labelKey: "labelTitleDisplay",
        optionType: "defaultProperty",
        options: EnumHelper.toArray(ObjectModelBuiltinPropertyType).map(type => {
            return {client_id: type, model_id: objectModel.id, labelTitleDisplay: ObjectModelBuiltinPropertyTypeName[type]}
          }
        )
      }
    },
    tryRemoveDisplayProperty(property) {
      //Check if default or regular property
      if (!(property instanceof  ObjectModelDefaultPropertyReference)){
        const arr = [...this.selectedObjectModelRelation.related_object_model_display_properties].filter(
          p => property.client_id !== p
        );
        this.selectedObjectModelRelation.related_object_model_display_properties = arr;
      }else{
        const prop = this.selectedObjectModelRelation.relatedObjectModelDisplayDefaultPropertiesArray.find(
          p => property.type === p.type
        );
        prop.destroy()
      }
    },
    tryRemoveParentDisplayProperty(property) {
      if (!(property instanceof  ObjectModelDefaultPropertyReference)){
        const arr = [...this.selectedObjectModelRelation.parent_object_model_display_properties].filter(
          p => property.client_id !== p
        );
        this.selectedObjectModelRelation.parent_object_model_display_properties = arr;
      }else{
        const prop = this.selectedObjectModelRelation.parentObjectModelDisplayDefaultPropertiesArray.find(
          p => property.type === p.type
        );
        prop.destroy()
      }
    },
    onSelectDisplayProperty(value, model): void {
      if (value.optionType === "defaultProperty"){
        let valueJson = {type: value.client_id, model_id: value.model_id};
        if (
          this.selectedObjectModelRelation.relatedObjectModelDisplayDefaultPropertiesArray.filter(p => p.type === valueJson.type ).length > 1||
          this.selectedRelatedPropertyIds(model).length > 3
        ) {
          return
        }else{
          this.selectedObjectModelRelation.appendRelatedObjectModelDisplayDefaultProperty(valueJson);
        }
      }else{

        if (
          this.selectedObjectModelRelation.related_object_model_display_properties.includes(value.client_id) ||
          this.selectedRelatedPropertyIds(model).length > 3
        ) {
          return;
        }
        this.selectedObjectModelRelation.related_object_model_display_properties.push(value.client_id);
      }

    },
    onSelectParentDisplayProperty(value): void {
      if (value.optionType === "defaultProperty") {
        let valueJson = {type: value.client_id, model_id: value.model_id};
        if (
          this.selectedObjectModelRelation.parentObjectModelDisplayDefaultPropertiesArray.filter(p => p.type === valueJson.type ).length > 1 ||
          this.selectedParentPropertyIds.length > 3
        ) {
          return
        } else {
          this.selectedObjectModelRelation.appendParentObjectModelDisplayDefaultProperty(valueJson);
        }
      } else {
        if (
          this.selectedObjectModelRelation.parent_object_model_display_properties.includes(value.client_id) ||
          this.selectedParentPropertyIds.length > 3
        ) {
          return;
        }
        this.selectedObjectModelRelation.parent_object_model_display_properties.push(value.client_id);
      }
    },
    onSelectListView(modelId, listViewId): void {
      const listViewObj = this.selectedObjectModelRelation.relatedObjectModelListViewIdsObj;
      listViewObj[modelId] = listViewId;
      this.selectedObjectModelRelation.setRelatedObjectModelListViewIds(listViewObj);
    },
    addModelsToRepo(response: IServerData<IObjectModelsSearchResponse>): void {
      const data = response && response[0];
      if (data && data.success) {
        ObjectModel.provider.createOrUpdateManyFrom(data.object_models);
      }
    },
    tryRemoveRelatedObjectModel(id: string) {
      this.selectedRelatedObjectModelId = id;
      const changeDetector = new ChangeDetector({
        left: m => m.related_object_model_ids,
        right: m => m?.related_object_model_ids,
        shouldCompare: (left, right, detector) => {
          return !!left && !!right;
        },
        isSame(left, right, detector) {
          // if it's not included in the list of related object models for the published relation
          // then consider it ok to delete the related object model link
          return !right.includes(id);
        },
        onSame: (left, right, detector) => {
          this.removeSelectedRelatedObjectModel();
        },
        onDifference: (left, right, detector) => {
          this.showWarningModal();
        },
        onOtherwise: (left, right, detector) => {
          this.removeSelectedRelatedObjectModel();
        }
      });
      changeDetector.compare(this.selectedObjectModelRelation, this.selectedObjectModelRelation.publishedEquivalent);
    },
    removeSelectedRelatedObjectModel() {
      this.selectedObjectModelRelation.removeRelatedObjectModel(this.selectedRelatedObjectModelId);
      this.selectedRelatedObjectModelId = null;
    },
    showWarningModal() {
      this.showModal(this.vueModalID.confirmChange);
    },
    hideWarningModal() {
      this.hideModal(this.vueModalID.confirmChange);
    },
    handleConfirmation(data) {
      this.hideWarningModal();
      const action = $(data.event.currentTarget).data("action");
      if (action === "affirm") {
        this.removeSelectedRelatedObjectModel();
      }
    },
    createUserTypeFilter(option) {
      // if "Anyone in the organization" is clicked
      if (option.optionType === null ||
          option.optionType === parseInt(this.anyoneInAppVOption.value) ||
          this.isOnlySelectedOption(this.anyoneInAppVOption)) {
        this.selectedObjectModelRelation.userTypeFiltersHandler.forEach(filter => filter.destroy())
      }

      if (option.optionType === null) return;

      const filter = new ObjectModelRelationUserTypeFilterPermittee(this.selectedObjectModelRelation, this.getAssigneePropsFromOption(option))
      this.selectedObjectModelRelation.initializeEmbedded(this.selectedObjectModelRelation, filter)
      return filter
    },
    destroyUserTypeFilter(option) {
      //  "Anyone in the organization" is clicked
      if (option.optionType === null) {
        this.selectedObjectModelRelation.userTypeFiltersHandler.forEach(filter => filter.destroy())
      } else {
        this.destroyAssignee(option)
      }
    },
    // required by mixin but not used here
    removeAssignee(filter) {
      filter.destroy()
    },
  }
});
</script>
